// Applies broker theme colors as CSS variables on documentElement:
//   primaryColor → --primary, --ring, --primary-foreground (CTAs, hero, headings)
//   accentColor  → --brand-accent (eyebrows, step numbers, trust icons)
// Both are independent · a broker can override one without the other.
//
// Layering:
//   1. Global NexoFácil palette (defined in index.css :root) · always present.
//   2. Vertical default (e.g. proteção veicular's corporate blue). Applied
//      when the vertical declares `defaultTheme`. Optional per vertical.
//   3. Broker-level override (broker.pageContent.theme) · wins over both.
//
// Calling with both args lets the caller merge once instead of patching
// document twice. When neither has values, nothing is touched.

/** Convert "#RRGGBB" to `"H S% L%"` (the format Tailwind's `hsl(var(--x))` expects). */
export function hexToHsl(hex: string): string | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Picks white or near-black foreground based on perceived luminance. */
function pickForeground(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '0 0% 100%';
  const n = parseInt(m[1]!, 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.6 ? '216 50% 12%' : '0 0% 100%';
}

export type ThemeInput = {
  primaryColor?: string;
  accentColor?: string;
} | null | undefined;

export function applyBrokerTheme(
  /** Vertical-level default (lower precedence). */
  verticalDefault: ThemeInput,
  /** Broker-level override (higher precedence). */
  brokerOverride?: ThemeInput,
): void {
  const merged = {
    primaryColor: brokerOverride?.primaryColor ?? verticalDefault?.primaryColor,
    accentColor: brokerOverride?.accentColor ?? verticalDefault?.accentColor,
  };
  if (!merged.primaryColor && !merged.accentColor) return;

  const root = document.documentElement;
  if (merged.primaryColor) {
    const hsl = hexToHsl(merged.primaryColor);
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--ring', hsl);
      root.style.setProperty('--primary-foreground', pickForeground(merged.primaryColor));
    }
  }
  if (merged.accentColor) {
    const hsl = hexToHsl(merged.accentColor);
    if (hsl) root.style.setProperty('--brand-accent', hsl);
  }
}
